import SettingsSearchTarget from './SettingsSearchTarget';
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, TextInput, View } from 'react-native';
import moment from 'moment-mini';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useAuthStore } from '@/store';
import { accountApi } from '@/lib/api';
import { useTranslation } from '@/hooks/useTranslation';
import { readUsageCache, subscribeUsageCache, loadCachedUsage, invalidateUsageCache } from '@/lib/usageCache';
import UsageDailyChart from './UsageDailyChart';
import { UsageButton, UsageSegments } from './UsageControls';
import UsageHistoryGroup from './UsageHistoryGroup';
import UsageActivityBreakdown from './UsageActivityBreakdown';
import { buildUsageInsights, formatCreditChange, formatUsageNumber as number, loadUsageWindow, modelKey, groupUsageHistory } from './usageInsights';
import { formatCreditBalance } from '@/lib/formatCredits';

const percentage = value => {
  if (value > 0 && value < 0.1) return '<0.1%';
  const rounded = Math.round(Number(value) * 10) / 10;
  return `${rounded.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
};

function UsageSettings() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const tx = (key, params) => t(`settings.usage.${key}`, params);
  const router = useRouter();
  const accountId = useAuthStore(state => state.user?.id);
  const cacheKey = `history:${moment().format('YYYY-MM-DD')}`;
  const balance = useAuthStore(state => state.user?.credits);
  const fetchCredits = useAuthStore(state => state.fetchUserCredits);
  const [days, setDays] = useState(30);
  const [panelWidth, setPanelWidth] = useState(0);
  const compact = panelWidth > 0 && panelWidth < 600;
  const [reload, setReload] = useState(0);
  const [cached, setCached] = useState(() => ({ accountId, key: cacheKey, ...readUsageCache(accountId, cacheKey) }));
  const snapshot = cached.accountId === accountId && cached.key === cacheKey ? cached : readUsageCache(accountId, cacheKey);
  const data = { rows: [], count: 0, now: new Date(), partial: true, ...snapshot.value, loading: snapshot.loading || !snapshot.value && !snapshot.error, error: snapshot.error };
  const [balanceError, setBalanceError] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [breakdownView, setBreakdownView] = useState('models');
  const [selectedDay, setSelectedDay] = useState(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [showAllModels, setShowAllModels] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20);
  const border = colors.border_color_secondary;
  const surface = colors.solid_color_block;
  const muted = colors.solid_color_secondary;
  const card = [styles.card, { backgroundColor: surface, borderColor: border }];

  useEffect(() => {
    let cancelled = false;
    const update = snapshot => setCached({ accountId, key: cacheKey, ...snapshot });
    update(readUsageCache(accountId, cacheKey));
    const unsubscribe = subscribeUsageCache(accountId, cacheKey, update);
    setBalanceError(false);
    fetchCredits({ force: reload > 0 }).then(result => {
      if (!cancelled && !result) setBalanceError(true);
    }).catch(() => { if (!cancelled) setBalanceError(true); });
    if (accountId) loadCachedUsage(accountId, cacheKey, async progress => {
      const now = new Date();
      const rows = await loadUsageWindow(accountApi.getUserInferenceCredits, {
        startDate: moment(now).startOf('day').subtract(29, 'days').toISOString(),
        endDate: moment(now).toISOString(),
        onProgress: (count, rows) => progress({ rows, count, now, partial: true }),
      });
      return { rows, count: rows.length, now, partial: false };
    }, { force: reload > 0 }).catch(() => {});
    return () => { cancelled = true; unsubscribe(); };
  }, [accountId, cacheKey, fetchCredits, reload]);

  const stats = useMemo(() => buildUsageInsights(data.rows, days, data.now), [data.rows, days, data.now]);
  const history = useMemo(() => groupUsageHistory(stats.rows.filter(row => {
    if (selectedModel && (!(Number(row.credits) < 0) || modelKey(row) !== selectedModel)) return false;
    if (selectedChat && row.conversationId !== selectedChat) return false;
    if (selectedDay && moment(row.date).format('YYYY-MM-DD') !== selectedDay) return false;
    if (kind === 'spent' && !(Number(row.credits) < 0)) return false;
    if (kind === 'added' && !(Number(row.credits) > 0)) return false;
    const search = query.trim().toLowerCase();
    return !search || [row.modelName, row.model, row.memo, row.entryType, row.conversationTitle, row.workTaskTitle].some(value => value?.toLowerCase().includes(search));
  })), [stats.rows, selectedModel, selectedChat, selectedDay, kind, query]);
  useEffect(() => { setVisibleCount(20); }, [days, selectedModel, selectedChat, selectedDay, kind, query, reload]);
  const changeRange = value => { setDays(value); setSelectedDay(null); setSelectedModel(null); setSelectedChat(null); };
  const label = row => row.entryType === 'voice' ? tx('voiceMode') : row.model || row.memo || (row.entryType === 'earn' ? tx('earned') : row.entryType === 'purchase' ? tx('purchase') : tx('otherUsage'));
  const modelLabel = name => name === 'Voice Mode' ? tx('voiceMode') : name === 'Other usage' ? tx('otherUsage') : name;
  const button = (title, onPress, active = false, extra = {}) => (
    <UsageButton onPress={onPress} active={active} {...extra}>{title}</UsageButton>
  );
  const ready = data.count > 0 || (!data.loading && !data.error);
  const top = stats.models[0];
  const max = Math.max(0, ...stats.daily.map(day => day.value));
  const focusDay = stats.daily.find(day => day.date === selectedDay);
  const allotment = balance?.monthlyAllotment;
  const balanceValue = balanceError ? '—' : formatCreditBalance(balance?.totalAvailable);
  const hasFilters = Boolean(selectedModel || selectedDay || selectedChat || query || kind !== 'all');
  const clearFilters = () => { setSelectedModel(null); setSelectedChat(null); setSelectedDay(null); setKind('all'); setQuery(''); };
  const allowanceUsed = Number(allotment?.used);
  const allowanceTotal = Number(allotment?.total);
  const showAllowance = !balanceError && allowanceTotal > 0 && Number.isFinite(allowanceUsed);
  const resetDate = allotment?.resetDate && moment(allotment.resetDate).isValid() ? moment(allotment.resetDate).format('MMM D') : null;
  const rangeLabel = `${moment(data.now).subtract(days - 1, 'days').format('MMM D')} – ${moment(data.now).format('MMM D, YYYY')}`;

  const balanceCard = (
    <View style={[styles.balance, { borderColor: border }]}>
      <View style={styles.between}>
        <Text size="s" color="secondary">{tx('design.availableBalance')}</Text>
        <Text size="l" weight="semibold" style={styles.numeric}>{balanceValue}</Text>
      </View>
      <Text size="xs" color="secondary" style={styles.description}>{balanceError ? tx('balanceFailed') : tx('monthlyPlusPurchased', { monthly: formatCreditBalance(allotment?.remaining), purchased: formatCreditBalance(balance?.purchased?.available) })}</Text>
      {showAllowance && <View style={styles.allowance}>
        <View style={styles.between}>
          <Text size="xs" color="secondary">{tx('design.allowanceUsed')}</Text>
          <Text size="xs" style={styles.numeric}>{formatCreditBalance(allowanceUsed)} / {formatCreditBalance(allowanceTotal)}</Text>
        </View>
        <View accessibilityRole="progressbar" accessibilityLabel={tx('allowanceExplanation')}
          accessibilityValue={{ min: 0, max: allowanceTotal, now: Math.max(0, Math.min(allowanceUsed, allowanceTotal)) }}
          style={[styles.track, { backgroundColor: muted }]}>
          <View style={{ width: `${Math.max(0, Math.min(100, allowanceUsed / allowanceTotal * 100))}%`, height: 4, backgroundColor: colors.text_color_secondary }} />
        </View>
        {!!resetDate && <Text size="xs" color="secondary">{tx('resets', { date: resetDate })}</Text>}
      </View>}
    </View>
  );

  return (
    <View testID="usage-settings" onLayout={event => setPanelWidth(event.nativeEvent.layout.width)} style={styles.root}>
      <View style={styles.toolbar}>
        <View style={styles.rangeControl}>
          <UsageSegments label={tx('design.dateRange')} value={days} onChange={changeRange}
            options={[7, 14, 30].map(value => ({ value, label: tx('design.days', { days: value }), accessibilityLabel: tx('lastDays', { days: value }) }))} />
        </View>
        {button(tx('refresh'), () => { invalidateUsageCache(accountId); setReload(value => value + 1); }, false,
          { disabled: data.loading, accessibilityLabel: tx('refresh'), icon: 'arrow-rotate-right' })}
      </View>

      {(data.loading || data.error) && data.count > 0 && <View style={[styles.notice, { backgroundColor: muted }]} accessibilityLiveRegion="polite">
        {data.loading && <ActivityIndicator size="small" color={colors.text_color_secondary} />}
        <Text size="s" color="secondary" style={{ flex: 1, lineHeight: 21 }}>{tx(!data.partial ? (data.error ? 'cachedRefreshFailed' : 'refreshingCached') : data.error ? 'partialHistoryFailed' : 'partialHistory', { count: number(data.count) })}</Text>
      </View>}

      <View style={card}>
        <View style={styles.overview}>
          <View style={styles.total}>
            <Text size="s" weight="medium">{tx('periodSpent')}</Text>
            <Text testID="usage-period-total" weight="semibold" numberOfLines={1} adjustsFontSizeToFit style={[styles.numeric, styles.totalNumber]}>{ready ? number(stats.spent) : '—'}</Text>
            <Text size="s" color="secondary">{rangeLabel}</Text>
          </View>
          {!compact && <View style={{ flexGrow: 1, flexBasis: 250 }}>{balanceCard}</View>}
        </View>

        {data.loading && !data.count ? <View style={styles.empty} accessibilityLiveRegion="polite">
          <ActivityIndicator color={colors.text_color_primary} /><Text color="secondary" size="s">{tx('loadingCompleteHistory', { count: number(data.count) })}</Text>
        </View> : data.error && !data.count ? <View style={styles.empty} accessibilityRole="alert">
          <Icon name="chart-column" size={24} color={colors.text_color_secondary} />
          <Text size="m" weight="semibold">{tx('insightsFailed')}</Text>
          <Text size="s" color="secondary" style={styles.emptyCopy}>{tx('insightsFailedDescription')}</Text>
          {button(tx('retry'), () => setReload(value => value + 1))}
        </View> : <>
          <View style={styles.chartHeading}>
            <SettingsSearchTarget label={tx('dailySpending')}><Text size="s" weight="medium" accessibilityRole="header" accessibilityLevel={2}>{tx('dailySpending')}</Text></SettingsSearchTarget>
            <Text size="xs" color="secondary">{focusDay ? `${moment(focusDay.date).format('MMM D')} · ${number(focusDay.value)} ${tx('creditsColumn')}` : `${number(max)} ${tx('creditsColumn')}`}</Text>
          </View>
          <UsageDailyChart key={days} days={stats.daily} selectedDay={selectedDay} onSelectDay={setSelectedDay}
            border={border} muted={muted} surface={colors.solid_color_primary} creditsLabel={tx('creditsColumn')} />
          <View style={styles.between}>
            <Text size="xs" color="secondary">{moment(stats.daily[0].date).format('MMM D')}</Text>
            <Text size="xs" color="secondary">{moment(stats.daily[Math.floor(days / 2)].date).format('MMM D')}</Text>
            <Text size="xs" color="secondary">{tx('today')}</Text>
          </View>
          <Text size="xs" color="secondary" style={styles.chartHint}>{tx('selectDay')}</Text>
        </>}

        {compact && <View style={{ marginTop: 20 }}>{balanceCard}</View>}

        <View style={[styles.metrics, { borderTopColor: border }]}>
          <View style={styles.metric}>
            <Text size="s" color="secondary">{tx('dailyAverage')}</Text>
            <Text size="xl" weight="semibold" style={styles.numeric}>{ready ? number(stats.spent / days) : '—'}</Text>
            <Text size="xs" color="secondary" style={styles.description}>{tx('averageDescription', { days })}</Text>
          </View>
          <View style={styles.metric}>
            <Text size="s" color="secondary">{tx('paidActivity')}</Text>
            <Text size="xl" weight="semibold" style={styles.numeric}>{ready ? number(stats.charges) : '—'}</Text>
            <Text size="xs" color="secondary" style={styles.description}>{ready ? tx('modelCount', { count: stats.models.length }) : tx('calculating')}</Text>
          </View>
        </View>
      </View>

      {(data.count > 0 || !data.loading && !data.error) && <>
        <View style={card}>
          <View style={styles.sectionHeading}>
            <SettingsSearchTarget label={tx('whereCreditsGo')}><Text size="l" weight="semibold" accessibilityRole="header" accessibilityLevel={2}>{tx('whereCreditsGo')}</Text></SettingsSearchTarget>
            <Text size="s" color="secondary" style={styles.description}>{tx(breakdownView === 'models' ? 'modelExplanation' : 'activityExplanation')}</Text>
          </View>
          <View style={styles.breakdownControl}>
            <UsageSegments label={tx('design.breakdown')} value={breakdownView} onChange={setBreakdownView}
              options={['models', 'chats', 'work'].map(value => ({ value, label: tx(`design.${value}`), accessibilityLabel: tx(`breakdown${value}`) }))} />
          </View>
          {breakdownView !== 'models' ? <UsageActivityBreakdown key={`${breakdownView}-${days}-${reload}`} accountId={accountId} refreshVersion={reload}
            type={breakdownView} rows={stats.rows} days={days} now={data.now} tx={tx} button={button} border={border} loading={data.loading}
            selectedChat={selectedChat} onSelectChat={id => { setSelectedChat(id); setSelectedModel(null); setKind('all'); }} /> : top ? <>
            <Text size="s" color="secondary" style={styles.insight}>{tx('topModelInsight', { model: modelLabel(top.name), percent: percentage(top.share), credits: number(top.credits) })}</Text>
            {(showAllModels ? stats.models : stats.models.slice(0, 5)).map((model, index) => <Pressable key={model.name}
              accessibilityRole="button" accessibilityState={{ selected: selectedModel === model.name }} accessibilityLabel={tx('filterModel', { model: modelLabel(model.name) })}
              onPress={() => { setSelectedModel(selectedModel === model.name ? null : model.name); setKind('all'); setSelectedChat(null); }}
              style={({ hovered, pressed, focused }) => [styles.modelRow, { borderColor: border, backgroundColor: selectedModel === model.name || hovered || pressed || focused ? muted : 'transparent' }]}>
              <Text size="xs" color="tertiary" style={[styles.rank, styles.numeric]}>{String(index + 1).padStart(2, '0')}</Text>
              <View style={styles.modelCopy}>
                <View style={styles.between}>
                  <Text size="s" weight="medium" style={{ flex: 1 }}>{modelLabel(model.name)}</Text>
                  <Text size="s" weight="semibold" style={styles.numeric}>{number(model.credits)}</Text>
                </View>
                <View style={[styles.track, { backgroundColor: muted }]}>
                  <View style={{ width: `${model.share}%`, height: 4, backgroundColor: colors.text_color_secondary }} />
                </View>
                <View style={[styles.between, { flexWrap: 'wrap' }]}>
                  <Text size="xs" color="secondary" style={{ flexShrink: 1 }}>{tx('modelDetail', { count: number(model.count), average: number(model.average) })}{model.voiceCredits > 0 ? ` · ${tx('voiceSpend', { credits: number(model.voiceCredits) })}` : ''}</Text>
                  <Text size="xs" color="secondary">{selectedModel === model.name ? tx('clearModel') : percentage(model.share)}</Text>
                </View>
              </View>
              <Icon name={selectedModel === model.name ? 'check' : 'chevron-right'} size={12} color={colors.text_color_secondary} />
            </Pressable>)}
            {stats.models.length > 5 && <View style={styles.more}>{button(showAllModels ? tx('showFewer') : tx('showAllModels', { count: stats.models.length }), () => setShowAllModels(value => !value), false, { icon: showAllModels ? 'chevron-up' : 'chevron-down' })}</View>}
          </> : <View style={styles.empty}>
            <Icon name="chart-column" size={24} color={colors.text_color_secondary} />
            <Text size="m" weight="semibold">{tx(data.loading || data.error ? 'noLoadedSpending' : 'noSpending')}</Text>
            <Text size="s" color="secondary" style={styles.emptyCopy}>{tx('noSpendingDescription')}</Text>
          </View>}
        </View>

        <View style={card}>
          <View style={styles.sectionHeading}>
            <SettingsSearchTarget label={tx('transactionHistory')}><Text size="l" weight="semibold" accessibilityRole="header" accessibilityLevel={2}>{tx('transactionHistory')}</Text></SettingsSearchTarget>
            <Text size="s" color="secondary">{tx('creditsAdded', { credits: number(stats.added) })}</Text>
          </View>
          <View style={[styles.search, { borderColor: border, backgroundColor: colors.solid_color_primary }]}>
            <Icon name="search" size={15} color={colors.text_color_secondary} />
            <TextInput accessibilityLabel={tx('searchActivity')} placeholder={tx('searchActivity')} placeholderTextColor={colors.text_color_secondary}
              value={query} onChangeText={setQuery} autoCorrect={false} style={[styles.searchInput, { color: colors.text_color_primary }]} />
            {!!query && <UsageButton accessibilityLabel={tx('design.clearSearch')} icon="xmark" onPress={() => setQuery('')} style={styles.clearSearch} />}
          </View>
          <View style={styles.historyControls}>
            <View style={styles.filterControl}>
              <UsageSegments label={tx('design.activityType')} value={kind} onChange={setKind}
                options={['all', 'spent', 'added'].map(value => ({ value, label: tx(`design.${value}`), accessibilityLabel: tx(`filter${value}`) }))} />
            </View>
            {hasFilters && button(tx('clearFilters'), clearFilters)}
          </View>
          {(selectedModel || selectedDay || selectedChat) && <View style={styles.filterChips}>
            {selectedModel && button(`${modelLabel(selectedModel)} ×`, () => setSelectedModel(null), true)}
            {selectedChat && button(`${tx('selectedChat')} ×`, () => setSelectedChat(null), true)}
            {selectedDay && button(`${moment(selectedDay).format('MMM D')} ×`, () => setSelectedDay(null), true)}
          </View>}
          <Text size="xs" color="secondary" style={styles.resultCount} accessibilityLiveRegion="polite">{tx('matchingTransactions', { count: number(history.length) })}</Text>
          {history.slice(0, visibleCount).map((row, index) => row.isUsageGroup
            ? <UsageHistoryGroup key={row.groupKey} row={row} tx={tx} button={button} border={border} />
            : <Pressable key={`${row.date}-${index}`} accessibilityRole={row.link ? 'link' : undefined} disabled={!row.link} onPress={() => router.push(row.link)}
              style={({ hovered }) => [styles.transaction, { borderColor: border, backgroundColor: hovered && row.link ? muted : 'transparent' }]}>
              <View style={[styles.transactionIcon, { backgroundColor: muted }]}><Icon name={row.credits > 0 ? 'plus' : row.entryType === 'voice' ? 'microphone' : 'sparkles'} size={14} color={colors.text_color_secondary} /></View>
              <View style={{ flex: 1, minWidth: 0, gap: 5 }}>
                <Text size="s" weight="medium">{label(row)}{row.link ? ' ↗' : ''}</Text>
                <Text size="xs" color="secondary" style={styles.description}>{moment(row.date).format('MMM D, YYYY · h:mm A')}{row.activityType === 'chat' && row.conversationTitle ? ` · ${row.conversationTitle}` : ''}{row.memo && row.memo !== label(row) ? ` · ${row.memo}` : ''}</Text>
              </View>
              <Text size="s" weight="semibold" color={row.credits > 0 ? 'success' : 'primary'} style={styles.numeric}>{formatCreditChange(row.credits)}</Text>
            </Pressable>)}
          {!history.length && <View style={styles.empty}>
            <Icon name="magnifying-glass" size={22} color={colors.text_color_secondary} />
            <Text size="s" color="secondary" style={styles.emptyCopy}>{tx('noMatchingActivity')}</Text>
          </View>}
          {history.length > visibleCount && <View style={styles.more}>{button(tx('loadMoreActivity', { count: number(history.length - visibleCount) }), () => setVisibleCount(value => value + 40))}</View>}
        </View>
      </>}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { gap: 24, paddingBottom: 24 },
  toolbar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
  rangeControl: { flexGrow: 1, flexShrink: 1, flexBasis: 210, minWidth: 210, maxWidth: 280 },
  card: { padding: 24, borderRadius: 16, borderWidth: 1, minWidth: 0 },
  overview: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 24, marginBottom: 28 },
  total: { flex: 1, minWidth: 200, gap: 6 },
  totalNumber: { fontSize: 44, lineHeight: 54, letterSpacing: -1.4 },
  balance: { flexGrow: 1, gap: 8, padding: 16, borderWidth: StyleSheet.hairlineWidth, borderRadius: 12 },
  allowance: { gap: 8, marginTop: 6 },
  description: { lineHeight: 19 },
  metrics: { flexDirection: 'row', flexWrap: 'wrap', gap: 24, borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 20, marginTop: 20 },
  metric: { flex: 1, minWidth: 115, gap: 6 },
  numeric: { fontVariant: ['tabular-nums'] },
  between: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  chartHeading: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, marginBottom: 4 },
  chartHint: { marginTop: 12, lineHeight: 19 },
  notice: { flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12, borderRadius: 12 },
  sectionHeading: { gap: 6, marginBottom: 18 },
  breakdownControl: { width: 360, maxWidth: '100%' },
  insight: { marginTop: 20, marginBottom: 8, lineHeight: 21 },
  track: { height: 4, borderRadius: 2, overflow: 'hidden' },
  modelRow: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingVertical: 18, paddingHorizontal: 4, borderBottomWidth: StyleSheet.hairlineWidth, borderRadius: 6 },
  rank: { width: 20 },
  modelCopy: { flex: 1, minWidth: 0, gap: 10 },
  more: { alignItems: 'center', marginTop: 12 },
  transaction: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 18, borderTopWidth: StyleSheet.hairlineWidth },
  transactionIcon: { width: 32, height: 32, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  search: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 10, paddingHorizontal: 12, minHeight: 48, marginBottom: 14 },
  searchInput: { flex: 1, minWidth: 0, paddingVertical: 13, fontSize: 14 },
  clearSearch: { paddingHorizontal: 8 },
  historyControls: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  filterControl: { width: 270, maxWidth: '100%' },
  filterChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  resultCount: { marginVertical: 18 },
  empty: { paddingVertical: 32, alignItems: 'center', gap: 12 },
  emptyCopy: { textAlign: 'center', lineHeight: 21 },
});
export default React.memo(UsageSettings);
